/**
 * Card de CFDI / Invoices pendientes. Muestra las transacciones del rango
 * que necesitan documento (CFDI subido o declarado externo) pero aún no
 * lo tienen. Útil tanto en Resumen (mes en curso) como en Análisis (rango
 * personalizado).
 *
 * Urgencia visual:
 *   verde:   0 pendientes
 *   warning: hay pendientes pero el SAT aún no cierra el periodo
 *   error:   ≤5 días hábiles para el deadline o ya vencido
 *
 * Deadline = día 17 del mes siguiente al periodo de operación (declaración
 * mensual SAT). Cuenta días hábiles excluyendo sábado/domingo.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import clsx from "clsx";
import { api } from "@/lib/api";
import { formatMxnCents } from "@/lib/format";
import { ChartCard } from "@/features/finance/ChartCard";

export interface PendingTx {
  id: string;
  kind: "ingreso" | "gasto" | "transferencia";
  date: string;
  paymentDate: string | null;
  subtotalCents: number;
  ivaCents: number;
  totalCents: number;
  invoiceStatus: string;
  documentedExternally?: boolean;
  title: string | null;
  counterparty: string | null;
  description: string | null;
  companyName: string | null;
  documents?: { id: string }[];
}

export function PendingInvoicesCard({
  from,
  to,
  queryKey,
}: {
  from: string;
  to: string;
  /** Key adicional para evitar colisión entre instancias (resumen vs analisis). */
  queryKey: string;
}) {
  const qs = `from=${from}&to=${to}&affectsTotals=1`;

  const allRangeQ = useQuery<{ transactions: PendingTx[] }>({
    queryKey: ["finance", "all-range-for-pending", queryKey, from, to],
    queryFn: () => api.get(`/finance/transactions?${qs}`),
  });

  const allRange = allRangeQ.data?.transactions ?? [];
  const pendientes = allRange.filter((t) => {
    if (t.kind === "transferencia") return false;
    if (t.invoiceStatus === "no_aplica" || t.invoiceStatus === "olvidado") return false;
    if (t.documentedExternally) return false;
    const docCount = t.documents?.length ?? 0;
    return docCount === 0;
  });
  const olvidadas = allRange.filter(
    (t) => t.kind !== "transferencia" && t.invoiceStatus === "olvidado",
  );

  // Periodo más viejo entre los pendientes — define el deadline más urgente.
  const byPeriod = new Map<string, number>();
  for (const t of pendientes) {
    const p = (t.paymentDate ?? t.date).slice(0, 7);
    byPeriod.set(p, (byPeriod.get(p) ?? 0) + 1);
  }
  const periods = [...byPeriod.keys()].toSorted();

  const totalCount = pendientes.length;
  const totalIng = pendientes.reduce((s, t) => (t.kind === "ingreso" ? s + t.totalCents : s), 0);
  const totalGas = pendientes.reduce((s, t) => (t.kind === "gasto" ? s + t.totalCents : s), 0);

  // Urgencia según deadline más cercano.
  const today = new Date();
  let urgency: "ok" | "warning" | "error" = "ok";
  let urgencyMsg = "";
  if (totalCount === 0) {
    urgency = "ok";
    urgencyMsg = "Todo facturado";
  } else if (periods.length > 0) {
    const oldest = periods[0]!;
    const [y, m] = oldest.split("-").map(Number);
    // Día 17 del mes siguiente (declaración SAT mensual).
    const deadline = new Date(y!, m!, 17);
    const businessDaysLeft = businessDaysBetween(today, deadline);
    if (businessDaysLeft <= 0) {
      urgency = "error";
      urgencyMsg = `Vencido el ${deadline.toLocaleDateString("es-MX", { day: "numeric", month: "long" })} · declaración SAT ya pasó`;
    } else if (businessDaysLeft <= 5) {
      urgency = "error";
      urgencyMsg = `Faltan ${businessDaysLeft} días hábiles para el cierre SAT del periodo más viejo`;
    } else {
      urgency = "warning";
      urgencyMsg = `Cierre SAT del periodo más viejo en ${businessDaysLeft} días hábiles`;
    }
  }

  const cardClasses =
    urgency === "ok"
      ? "border border-success/30 bg-success/5"
      : urgency === "warning"
        ? "border border-warning/40 bg-warning/5"
        : "border border-error/50 bg-error/5";

  return (
    <ChartCard
      title="CFDI e Invoices pendientes"
      className={cardClasses}
      actions={
        totalCount > 0 ? (
          <span
            className={clsx(
              "badge badge-sm tabular-nums",
              urgency === "error"
                ? "badge-error"
                : urgency === "warning"
                  ? "badge-warning"
                  : "badge-ghost",
            )}
          >
            {totalCount}
          </span>
        ) : null
      }
    >
      {totalCount === 0 ? (
        <p className="text-sm text-success">
          Todas las transacciones del rango están facturadas o no requieren CFDI.
        </p>
      ) : (
        <>
          <div
            className={clsx(
              "text-xs flex items-start gap-2",
              urgency === "error" ? "text-error font-medium" : "text-warning",
            )}
          >
            <AlertTriangle className="size-4 shrink-0 mt-0.5" />
            <span>{urgencyMsg}</span>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-base-300/40">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-base-content/55 font-semibold">
                Ingresos sin CFDI emitido
              </div>
              <div className="font-display font-bold text-base tabular-nums text-success">
                {formatMxnCents(totalIng)}
              </div>
              <div className="text-[11px] text-base-content/55">
                {pendientes.filter((t) => t.kind === "ingreso").length} tx
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-base-content/55 font-semibold">
                Gastos sin CFDI recibido
              </div>
              <div className="font-display font-bold text-base tabular-nums text-error">
                {formatMxnCents(totalGas)}
              </div>
              <div className="text-[11px] text-base-content/55">
                {pendientes.filter((t) => t.kind === "gasto").length} tx
              </div>
            </div>
          </div>

          <ul className="pt-2 border-t border-base-300/40 -mx-2">
            {[...pendientes]
              .toSorted((a, b) => {
                if (a.kind !== b.kind) return a.kind === "ingreso" ? -1 : 1;
                return (a.paymentDate ?? a.date).localeCompare(b.paymentDate ?? b.date);
              })
              .map((t) => {
                const isIng = t.kind === "ingreso";
                // Nombre: prioridad título → empresa → contraparte. Descripción
                // se muestra debajo si aporta info distinta al nombre.
                const name =
                  t.title?.trim() ||
                  t.companyName?.trim() ||
                  t.counterparty?.trim() ||
                  t.description?.trim() ||
                  "Sin descripción";
                const desc = t.description?.trim();
                const showDesc = desc && desc !== name;
                const flowDate = t.paymentDate ?? t.date;
                const docType = inferDocType(t);
                return (
                  <li
                    key={t.id}
                    className="grid grid-cols-[auto_3.5rem_1fr_auto_auto] items-center gap-2 px-2 py-1.5 text-xs rounded odd:bg-base-content/[0.03]"
                    title={`${t.kind} · ${flowDate}`}
                  >
                    <span
                      className={clsx("size-1.5 rounded-full", isIng ? "bg-success" : "bg-error")}
                      aria-hidden
                    />
                    <span
                      className={clsx(
                        "badge badge-xs text-[9px] font-normal justify-self-center",
                        docType === "CFDI" ? "badge-info" : "badge-warning",
                      )}
                      title={
                        docType === "CFDI"
                          ? "CFDI mexicano (SAT)"
                          : "Invoice extranjero (proveedor sin CFDI mx)"
                      }
                    >
                      {docType}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate leading-tight">{name}</div>
                      {showDesc && (
                        <div
                          className="text-[10px] text-base-content/50 truncate leading-tight mt-0.5"
                          title={desc}
                        >
                          {desc}
                        </div>
                      )}
                    </div>
                    <span className="text-base-content/55 tabular-nums font-mono text-[10px]">
                      {flowDate.slice(5)}
                    </span>
                    <span
                      className={clsx(
                        "tabular-nums font-medium text-right",
                        isIng ? "text-success" : "text-error",
                      )}
                    >
                      {isIng ? "+" : "−"}
                      {formatMxnCents(t.totalCents)}
                    </span>
                  </li>
                );
              })}
          </ul>
        </>
      )}

      {olvidadas.length > 0 && (
        <div className="pt-2 border-t border-base-300/40 text-xs flex items-center gap-2">
          <span className="badge badge-xs badge-error">{olvidadas.length} olvidadas</span>
          <span className="text-base-content/55">Deducción perdida — ya pasó la ventana SAT</span>
        </div>
      )}
    </ChartCard>
  );
}

/**
 * Mapea invoice_status al tipo de documento que aún falta:
 *   facturado  → "CFDI" (ya está emitido / debe estar emitido en SAT,
 *                falta solo subirlo al sistema)
 *   pendiente  → "Invoice" (asumimos extranjero por ahora — el usuario
 *                puede mover a 'facturado' o 'deducible_sin_factura'
 *                cuando confirme el caso)
 *   otro       → fallback CFDI
 */
function inferDocType(t: { invoiceStatus: string }): "CFDI" | "Invoice" {
  if (t.invoiceStatus === "pendiente") return "Invoice";
  return "CFDI";
}

/**
 * Días hábiles (lun-vie) entre dos fechas. Negativo si la fecha de fin
 * ya pasó. No considera días feriados nacionales — versión simple.
 */
function businessDaysBetween(from: Date, to: Date): number {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  if (end < start) return -1;
  let days = 0;
  const cur = new Date(start);
  while (cur < end) {
    cur.setDate(cur.getDate() + 1);
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) days += 1;
  }
  return days;
}
